import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { Switch } from 'src/design/atoms/Switch';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminUsers } from 'src/api/queries/organization/useAdminUsers';
import { formatDateTime } from 'src/utils';
import {
  DivisionDto,
  OrganizationOfficeDto,
  UserDto,
} from 'src/api/FiretrolApiClient';
import { DivisionList } from 'src/app/organization/DivisionList';
import { OfficeList } from 'src/app/organization/OfficeList';
import { useOrganization } from 'src/hooks/useOrganization';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { BadgeShortList } from 'src/design/molecules/BadgeShortList';
import { Header } from 'src/design/organisms/Header';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';
import { useAuth } from 'src/hooks/useAuth';

const colorLookup: Record<string, string> = {
  invited: 'yellow',
  confirmed: 'green',
  disabled: 'gray',
  archived: 'dark',
};

const displayStatus = (user: UserDto): string =>
  user.isArchived ? 'archived' : user.status;

const getOffices = (user: UserDto): OrganizationOfficeDto[] =>
  user.userOrganizationOffice
    ?.map((o) => o.organizationOffice!)
    .filter(Boolean) ?? [];

const getDivisions = (user: UserDto): DivisionDto[] =>
  user.userDivision?.map((d) => d.division!).filter(Boolean) ?? [];

const getColumns = (canSeeLoginHistory: boolean): MRT_ColumnDef<UserDto>[] => {
  return (
    [
      {
        accessorKey: 'fullName',
        header: 'Name',
      },
      {
        accessorKey: 'email',
        header: 'Email',
      },
      {
        accessorKey: 'classificationName',
        header: 'Classification',
      },
      {
        accessorKey: 'classificationLevelName',
        header: 'Level',
      },
      {
        accessorKey: 'supervisor.fullName',
        header: 'Supervisor',
      },
      canSeeLoginHistory && {
        header: 'Last Login',
        id: 'lastLogin',
        accessorFn: (row) => {
          return row.lastLogin ? formatDateTime(row.lastLogin) : '-';
        },
      },
      {
        id: 'status',
        header: 'Status',
        filterFn: 'contains',
        enableColumnFilterModes: false,
        accessorFn: displayStatus,
        Cell: ({ cell }) => {
          const status = displayStatus(cell.row.original);
          return <Badge color={colorLookup[status]}>{status}</Badge>;
        },
      },
      {
        id: 'offices',
        header: 'Offices',
        filterFn: 'contains',
        enableColumnFilterModes: false,
        accessorFn: (row) => {
          return getOffices(row)
            .map((o) => o.name)
            .join(', ');
        },
        Cell: ({ cell }) => {
          const user = cell.row.original;
          return <OfficeList offices={getOffices(user)} />;
        },
      },
      {
        id: 'divisions',
        header: 'Divisions',
        filterFn: 'contains',
        enableColumnFilterModes: false,
        accessorFn: (row) => {
          return getDivisions(row)
            .map((d) => d.name)
            .join(', ');
        },
        Cell: ({ cell }) => {
          const user = cell.row.original;
          return <DivisionList divisions={getDivisions(user)} />;
        },
      },
      {
        id: 'roles',
        header: 'Roles',
        filterFn: 'contains',
        enableColumnFilterModes: false,
        accessorFn: (row) => {
          return row.userOrganizationRole
            ?.map((r) => r.organizationRole?.name)
            .join(', ');
        },
        Cell: ({ cell }) => {
          const user = cell.row.original;
          const badges = user.userOrganizationRole?.map((r) => (
            <Badge key={r.id} mr="xs">
              {r.organizationRole?.name}
            </Badge>
          ));

          return <BadgeShortList items={badges} maxItems={1} />;
        },
      },
      {
        id: 'allowedJobIds',
        header: 'Allowed Jobs',
        filterFn: 'contains',
        enableColumnFilterModes: false,
        accessorFn: (row) => {
          if (!row.disableDivisionAccess) return 'All Division Jobs';
          const count =
            (row.allowedJobIds?.length ?? 0) +
            (row.allowedCtcJobNumbers?.length ?? 0);
          return `${count} Jobs`;
        },
        Cell: ({ cell }) => {
          const user = cell.row.original;
          if (!user.disableDivisionAccess)
            return <Badge>All Division Jobs</Badge>;
          const count =
            (user.allowedJobIds?.length ?? 0) +
            (user.allowedCtcJobNumbers?.length ?? 0);
          return <Badge>{`${count} Jobs`}</Badge>;
        },
      },
    ] as (MRT_ColumnDef<UserDto> | false)[]
  ).filter(Boolean) as MRT_ColumnDef<UserDto>[];
};

export const Users: React.FC = () => {
  const navigate = useNavigate();
  const { users, loading, refreshUsers } = useOrganization();
  const { isAdmin, checkFeatureAllowed } = useAuth();
  const [showArchived, setShowArchived] = React.useState(false);
  const { data: visibleUsers = users } = useAdminUsers(showArchived);

  const canSeeLoginHistory =
    checkFeatureAllowed(['settings_all', 'settings_login_history']) || isAdmin;

  React.useEffect(() => {
    refreshUsers();
  }, []);

  const columns = useMemo(() => {
    return getColumns(canSeeLoginHistory);
  }, [canSeeLoginHistory]);

  const tableState = useTableState({
    identifier: `users`,
    columns: columns,
    defaultSorting: [{ id: 'fullName', desc: false }],
  });

  const tableExports = useTableExports<UserDto>('users', (data) => {
    return data.map((item) => ({
      ['Name']: item.fullName,
      ['Email']: item.email,
      ['Classification']: item.classificationName || '',
      ['Level']: item.classificationLevelName || '',
      ['Supervisor']: item.supervisor?.fullName || '',
      ...(canSeeLoginHistory && {
        ['Last Login']: item.lastLogin ? formatDateTime(item.lastLogin) : '-',
      }),
      ['Status']: displayStatus(item),
      ['Offices']:
        getOffices(item)
          .map((o) => o.name)
          .join(' | ') || '',
      ['Divisions']:
        getDivisions(item)
          .map((d) => d.name)
          .join(' | ') || '',
      ['Roles']:
        item.userOrganizationRole
          ?.map((r) => r.organizationRole?.name)
          .join(' | ') || '',
      ['Allowed Jobs']: item.disableDivisionAccess
        ? `${
            (item.allowedJobIds?.length ?? 0) +
            (item.allowedCtcJobNumbers?.length ?? 0)
          } Jobs`
        : 'All Division Jobs',
    }));
  });

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 150 } },
      renderRowActions: ({ row }) => {
        const user = row.original;
        return (
          <Group>
            <EditActionIcon
              onClick={() => navigate(`/settings/users/edit/${user.id}`)}
            />
          </Group>
        );
      },
      enableClickToCopy: false,
      columns: columns,
      data: visibleUsers,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      renderTopToolbarCustomActions: () => (
        <Group>
          <CreateButton onClick={() => navigate('/settings/users/create')}>
            Add User
          </CreateButton>
          <Switch
            label="Show archived"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.currentTarget.checked)}
          />
        </Group>
      ),
    },
    getRowId: (row) => String(row.id),
    totalCount: visibleUsers.length,
    isLoading: loading,
    isFetching: false,
    isError: false,
    ...tableExports,
    onRefresh: refreshUsers,
  });

  return (
    <>
      <Header
        title="Users"
        breadcrumbProps={{ items: [{ text: 'Settings' }, { text: 'Users' }] }}
        helpText="Users can login to the system and access the divisions and jobs they are assigned to."
      />
      {table}
    </>
  );
};
